import React, { forwardRef, Ref, useEffect, useState } from 'react';
import { ApiAssignmentUser } from '../api';
import { Avatar, Button, SalvButton, Size } from '@salvapp/ui-components';
import { Dropdown } from 'react-bootstrap';
import './AlertRowAssignment.scss';
import { useSelector } from 'react-redux';
import { getUserId } from '../auth/authReducer';
import { AppState } from '../store/store';
import Has from '../auth/Has';
import { CAN_MODIFY_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER, CAN_MODIFY_SANCTION_ALERT } from '../auth/permissions';
import isEqual from 'lodash.isequal';
import { DropdownWrapper } from '../common/DropdownWrapper';
import { useAssignableUsers } from '../users/useAssignableUsers';
import type { AlignDirection } from 'react-bootstrap/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserXmark } from '@fortawesome/pro-solid-svg-icons/faUserXmark';
import { InternalApiUserBasicDetails } from '../api-internal';

export interface AlertRowAssignmentProps {
  assignee?: Omit<ApiAssignmentUser, 'id'>;
  defaultSize?: number;
  onChange: (user: InternalApiUserBasicDetails) => void;
  onUnassign?: () => void;
  sizeClass?: string;
  dropdownAlign?: AlignDirection;
}

interface ToggleProps {
  onClick: () => void;
}

export const AlertAssignmentDropdown = ({
  assignee,
  defaultSize = 40,
  onChange,
  onUnassign,
  sizeClass = '',
  dropdownAlign = 'end',
}: AlertRowAssignmentProps) => {
  const { assignableUsers } = useAssignableUsers();
  const userId = useSelector((state: AppState) => getUserId(state));
  const user = assignableUsers?.find((u) => u.id === userId);
  const [displayedAssignments, setDisplayedAssignments] = useState(assignableUsers?.slice(0, defaultSize));

  useEffect(() => {
    setDisplayedAssignments(assignableUsers?.slice(0, defaultSize));
  }, [assignableUsers, defaultSize]);

  const Toggle = forwardRef(({ onClick }: ToggleProps, ref: Ref<HTMLButtonElement>) => {
    return (
      <Has anyPermissions={[CAN_MODIFY_ALERT, CAN_MODIFY_SANCTION_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
        {(can: boolean) =>
          can ? (
            (assignee && (
              <SalvButton
                variant='muted'
                size={Size.SMALL}
                className={`d-flex flex-row align-items-center dropdown-toggle ${sizeClass}`}
                ref={ref}
                onClick={onClick}
                label={assignee.name}
                avatarEmail={assignee.email}
              />
            )) || (
              <SalvButton
                variant='muted'
                size={Size.SMALL}
                ref={ref}
                onClick={onClick}
                className={`d-flex align-items-center dropdown dropdown-toggle ${sizeClass}`}
                label='Assign'
              />
            )
          ) : (
            <div>
              {assignee && (
                <div>
                  <Avatar email={assignee.email} />
                  <span className='ms-2'>{assignee.name}</span>
                </div>
              )}
            </div>
          )
        }
      </Has>
    );
  });

  Toggle.displayName = 'Toggle';

  const handleAssignToMe = () => {
    if (user) onChange(user);
  };

  const searchUsers = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase().trim().replaceAll(' ', '');
    const filteredUsers = assignableUsers?.filter(
      (user) =>
        user.email &&
        (user.email.toLowerCase().includes(query) ||
          user.name.toLocaleLowerCase().trim().replaceAll(' ', '').includes(query))
    );
    if (filteredUsers && filteredUsers.length == 0 && displayedAssignments?.length == 0) {
      return;
    } else if (isEqual(filteredUsers, displayedAssignments)) {
      return;
    }
    setDisplayedAssignments(filteredUsers);
  };

  return (
    <DropdownWrapper className='assignee'>
      <Dropdown.Toggle as={Toggle} id='chooseAssignee' />
      <Dropdown.Menu align={{ sm: dropdownAlign }}>
        <div className='row px-4'>
          <div className='col dropdown-search-assignee'>
            <input
              type='text'
              className='form-control form-control-sm'
              placeholder='Search ....'
              onChange={(e) => searchUsers(e)}
              autoFocus
            />
          </div>
          {user && (
            <Dropdown.Item className='col assign-self-button'>
              <Button size='medium' variant='outline-primary' onClick={handleAssignToMe}>
                Assign to me
              </Button>
            </Dropdown.Item>
          )}
        </div>
        {onUnassign ? (
          <>
            <Dropdown.Item
              as='button'
              className='d-flex flex-row align-items-center dropdown-item-regular mt-1'
              onClick={() => onUnassign()}
            >
              <FontAwesomeIcon aria-label='Unassign' icon={faUserXmark} />
              <div className='d-flex flex-column'>
                <div className='ms-2'>Unassign</div>
              </div>
            </Dropdown.Item>
            <Dropdown.Divider className='m-1' />
          </>
        ) : (
          <Dropdown.Divider />
        )}
        <div className='items'>
          {displayedAssignments?.map((user) => (
            <Dropdown.Item
              key={user.id}
              as='button'
              className='d-flex flex-row align-items-center dropdown-item-regular'
              onClick={() => onChange(user)}
            >
              <Avatar email={user.email} />
              <div className='d-flex flex-column'>
                <div className='ms-2'>{user.name}</div>
                <div className='text-muted ms-2 small'>{user.email}</div>
              </div>
            </Dropdown.Item>
          ))}
        </div>
      </Dropdown.Menu>
    </DropdownWrapper>
  );
};

export default AlertAssignmentDropdown;
